import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer, combineLatest, Subject } from 'rxjs';
import { switchMap, shareReplay, tap, map } from 'rxjs/operators';
import { JobStageIssueService } from './job-stage-issue.service';
import { JobStageIssueResponse } from '../models/job-stage-issue.models';
import { JobPriorityNotificationResponse, JobPriorityUpdateNotification } from '../models/job-priority-notification.models';
import { DispatchAlertNotificationResponse, DispatchAlertNotification } from '../models/dispatch-alert-notification.models';

@Injectable({
    providedIn: 'root'
})
export class NotificationStateService {
    private _jobStageIssues$ = new BehaviorSubject<JobStageIssueResponse[]>([]);
    private _jobPriorityNotifications$ = new BehaviorSubject<JobPriorityNotificationResponse[]>([]);
    private _dispatchAlertNotifications$ = new BehaviorSubject<DispatchAlertNotificationResponse[]>([]);
    private _isLoading$ = new BehaviorSubject<boolean>(false);
    private _issueResolved$ = new Subject<{ serialNumber: string }>();

    // Public observables
    public readonly jobStageIssues$ = this._jobStageIssues$.asObservable();
    public readonly jobPriorityNotifications$ = this._jobPriorityNotifications$.asObservable();
    public readonly dispatchAlertNotifications$ = this._dispatchAlertNotifications$.asObservable();
    public readonly isLoading$ = this._isLoading$.asObservable();
    public readonly issueResolved$ = this._issueResolved$.asObservable();

    // Combined notifications
    public readonly allNotifications$ = combineLatest([
        this.jobStageIssues$,
        this.jobPriorityNotifications$,
        this.dispatchAlertNotifications$
    ]).pipe(
        map(([issues, priorityNotifications, dispatchAlerts]) => ({
            issues,
            priorityNotifications,
            dispatchAlerts
        }))
    );

    // Total notification count observable
    public readonly totalNotificationCount$ = this.allNotifications$.pipe(
        map(({ issues, priorityNotifications, dispatchAlerts }) =>
            issues.length + priorityNotifications.length + dispatchAlerts.length
        )
    );

    // Auto-refresh every 2 minutes
    private readonly refreshInterval$ = timer(0, 120000).pipe(
        switchMap(() => this.loadJobStageIssues()),
        shareReplay(1)
    );

    constructor(private jobStageIssueService: JobStageIssueService) {
        // Start auto-refresh
        this.refreshInterval$.subscribe();
    }

    /**
     * Get current job stage issues (from cache)
     */
    getCurrentIssues(): JobStageIssueResponse[] {
        return this._jobStageIssues$.value;
    }

    /**
     * Get count of current issues
     */
    getIssuesCount(): number {
        return this._jobStageIssues$.value.length;
    }

    /**
     * Get current job priority notifications (from cache)
     */
    getCurrentPriorityNotifications(): JobPriorityNotificationResponse[] {
        return this._jobPriorityNotifications$.value;
    }

    /**
     * Get count of current priority notifications
     */
    getPriorityNotificationsCount(): number {
        return this._jobPriorityNotifications$.value.length;
    }

    /**
     * Get current dispatch alert notifications (from cache)
     */
    getCurrentDispatchAlerts(): DispatchAlertNotificationResponse[] {
        return this._dispatchAlertNotifications$.value;
    }

    /**
     * Get count of current dispatch alerts
     */
    getDispatchAlertsCount(): number {
        return this._dispatchAlertNotifications$.value.length;
    }

    /**
     * Get total notification count
     */
    getTotalNotificationCount(): number {
        return this.getIssuesCount() + this.getPriorityNotificationsCount() + this.getDispatchAlertsCount();
    }

    /**
     * Emit an event indicating a job issue for a serial was resolved.
     */
    notifyIssueResolvedBySerial(serialNumber: string): void {
        if (!serialNumber) { return; }
        this._issueResolved$.next({ serialNumber });
    }

    /**
     * Manually refresh issues
     */
    refreshIssues(): Observable<JobStageIssueResponse[]> {
        return this.loadJobStageIssues();
    }

    /**
     * Add job priority notification from SignalR
     */
    addJobPriorityNotification(notification: JobPriorityUpdateNotification): void {
        const priorityNotification: JobPriorityNotificationResponse = {
            id: `job-priority-${notification.JobId}-${Date.now()}`,
            jobId: notification.JobId,
            jobNumber: notification.JobNumber,
            newPriority: notification.NewPriority,
            dueDate: notification.DueDate ? new Date(notification.DueDate) : undefined,
            customerName: notification.CustomerName,
            message: notification.Message,
            timestamp: new Date(),
            read: false
        };

        const currentNotifications = this._jobPriorityNotifications$.value;
        this._jobPriorityNotifications$.next([priorityNotification, ...currentNotifications]);
    }

    /**
     * Mark priority notification as read
     */
    markPriorityNotificationAsRead(notificationId: string): void {
        const currentNotifications = this._jobPriorityNotifications$.value;
        const updatedNotifications = currentNotifications.map(notification =>
            notification.id === notificationId
                ? { ...notification, read: true }
                : notification
        );
        this._jobPriorityNotifications$.next(updatedNotifications);
    }

    /**
     * Mark all priority notifications as read
     */
    markAllPriorityNotificationsAsRead(): void {
        const currentNotifications = this._jobPriorityNotifications$.value;
        const updatedNotifications = currentNotifications.map(notification => ({
            ...notification,
            read: true
        }));
        this._jobPriorityNotifications$.next(updatedNotifications);
    }

    /**
     * Add dispatch alert notification from SignalR
     */
    addDispatchAlertNotification(notification: DispatchAlertNotification): void {
        const dispatchAlert: DispatchAlertNotificationResponse = {
            id: `dispatch-alert-${notification.JobId}-${Date.now()}`,
            alertType: notification.AlertType,
            jobId: notification.JobId,
            jobNumber: notification.JobNumber,
            dueTime: notification.DueTime,
            customerName: notification.CustomerName,
            freightMethod: notification.FreightMethod,
            message: notification.Message,
            timestamp: new Date(),
            read: false
        };

        const currentNotifications = this._dispatchAlertNotifications$.value;
        this._dispatchAlertNotifications$.next([dispatchAlert, ...currentNotifications]);
    }

    /**
     * Mark dispatch alert notification as read
     */
    markDispatchAlertAsRead(notificationId: string): void {
        const currentNotifications = this._dispatchAlertNotifications$.value;
        const updatedNotifications = currentNotifications.map(notification =>
            notification.id === notificationId
                ? { ...notification, read: true }
                : notification
        );
        this._dispatchAlertNotifications$.next(updatedNotifications);
    }

    /**
     * Mark all dispatch alert notifications as read
     */
    markAllDispatchAlertsAsRead(): void {
        const currentNotifications = this._dispatchAlertNotifications$.value;
        const updatedNotifications = currentNotifications.map(notification => ({
            ...notification,
            read: true
        }));
        this._dispatchAlertNotifications$.next(updatedNotifications);
    }

    /**
     * Load job stage issues from API
     */
    private loadJobStageIssues(): Observable<JobStageIssueResponse[]> {
        this._isLoading$.next(true);

        return this.jobStageIssueService.getJobStageIssues().pipe(
            tap((issues) => {
                // Only keep open issues (no resolvedAt)
                const openIssues = (issues || []).filter(i => !i.resolvedAt);
                this._jobStageIssues$.next(openIssues);
                this._isLoading$.next(false);
            }),
            tap({
                error: (error) => {
                    console.error('Error loading job stage issues:', error);
                    this._isLoading$.next(false);
                }
            })
        );
    }
} 